import { useState, useRef, useEffect } from 'react'
import JsBarcode from 'jsbarcode'
import { Download, Barcode } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function BarcodeGenerator() {
  const [value, setValue] = useState('')
  const [format, setFormat] = useState('CODE128')
  const [barcodeSvg, setBarcodeSvg] = useState(null)
  const svgRef = useRef(null)

  const generateBarcode = () => {
    if (!value.trim()) {
      alert('Please enter barcode data')
      return
    }

    try {
      // Create a container div if it doesn't exist
      let container = svgRef.current
      if (!container) {
        // Create container dynamically
        container = document.createElement('div')
        container.id = 'barcode-container'
      } else {
        // Clear previous barcode
        container.innerHTML = ''
      }
      
      // Create new SVG element
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      container.appendChild(svg)
      
      // Generate barcode
      JsBarcode(svg, value.trim(), {
        format: format,
        width: 2,
        height: 100,
        displayValue: true,
        fontSize: 16,
        margin: 10
      })
      
      setBarcodeSvg(svg)
    } catch (error) {
      alert('Error generating barcode: ' + error.message)
      console.error('Barcode generation error:', error)
    }
  }

  const downloadBarcode = (format) => {
    if (!barcodeSvg) return

    const svgData = new XMLSerializer().serializeToString(barcodeSvg)
    
    if (format === 'svg') {
      const blob = new Blob([svgData], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'barcode.svg'
      link.click()
      URL.revokeObjectURL(url)
    } else {
      // PNG download
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      img.onload = () => {
        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = 'barcode.png'
          link.click()
          URL.revokeObjectURL(url)
        })
      }
      
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Enter barcode data:</label>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="input w-full"
              placeholder="Enter barcode number or text"
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Barcode Format:</label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="input w-full"
            >
              <option value="CODE128">CODE 128</option>
              <option value="CODE39">CODE 39</option>
              <option value="EAN13">EAN-13</option>
              <option value="EAN8">EAN-8</option>
              <option value="UPC">UPC-A</option>
              <option value="ITF14">ITF-14</option>
              <option value="MSI">MSI</option>
              <option value="pharmacode">Pharmacode</option>
            </select>
          </div>

          <Button onClick={generateBarcode} variant="primary" className="w-full">
            <Barcode className="w-5 h-5 mr-2" />
            Generate Barcode
          </Button>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="text-center space-y-4">
            <div className="flex justify-center p-4 bg-gray-50 rounded-lg min-h-[150px]">
              <div ref={svgRef} id="barcode-container"></div>
            </div>
            {barcodeSvg && (
              <div className="flex gap-4">
                <Button onClick={() => downloadBarcode('png')} variant="primary" className="flex-1">
                  <Download className="w-5 h-5 mr-2" />
                  Download PNG
                </Button>
                <Button onClick={() => downloadBarcode('svg')} variant="outline" className="flex-1">
                  <Download className="w-5 h-5 mr-2" />
                  Download SVG
                </Button>
              </div>
            )}
            {!barcodeSvg && (
              <p className="text-gray-600 text-sm">Enter barcode data and click "Generate Barcode" to create your barcode</p>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}

